import type { ChangeRow } from '@/components/ui/change-rows';
import { formatPercent, formatUsd, formatUtcDateTime } from '@/utils/format';
import { diffLeaves, isPlainObject } from '@/utils/object';
import { COMPONENT_LABELS, compensationOf, DOWNTIME_DAY_LABELS, dayLabel, describeDowntimeRule, PROGRAM_TYPE_LABELS, SESSION_LABELS } from './mm-program-editing';
import type { DowntimeDay, DowntimeRuleInput, MarketMaker, MarketMakerInput, ProgramInput, RuleComponent } from './mm-program-schema';

export type ChangeKind = 'changed' | 'added' | 'removed';

export interface ChangeGroup {
  key: string;
  title: string;
  subtitle?: string;
  kind: ChangeKind;
  rows: ChangeRow[];
}

const MARKET_MAKER_FIELDS = ['name', 'email', 'bcc_emails', 'active'] as const;

const LABELS: Record<string, string> = {
  name: 'Name',
  email: 'Report email',
  bcc_emails: 'BCC',
  active: 'Status',
  type: 'Variant',
  start_date: 'Start',
  end_date: 'End',
  session: 'Session',
  bps: 'half-width',
  kpi: 'quote size',
  nbbo: 'uses NBBO',
  threshold: 'Threshold',
  compensation: 'Compensation',
  exponent_start: 'Exponent start',
  exponent: 'Exponent',
  nbb_provider: 'NBBO provider',
  nbb_instrument: 'NBBO instrument',
  'ov.b2_multiple': 'Open volume B2 size multiple',
  'ov.compensation_fraction': 'Open volume credit',
  ov: 'Open volume credit',
  'rules.live': 'Live in production',
  'rules.place_rest_ms': 'Place rest',
  'rules.move_rest_ms': 'Move rest',
  'rules.fill_linger_ms': 'Fill linger',
  'rules.max_skew_fraction': 'Max skew',
  'rules.volatility.rungs_bps': 'Volatility rungs',
  'rules.volatility.add_bps': 'Volatility added width',
  'rules.volatility.cooldown_s': 'Volatility cooldown',
  'rules.volatility.provider': 'Volatility source provider',
  'rules.volatility.instrument': 'Volatility source instrument',
  'rules.volatility': 'Widen on volatility',
  'rules.out_of_book.min_gap_s': 'Out of book minimum gap',
  'rules.out_of_book.full_s': 'Out of book full cut after',
  'rules.out_of_book.max_cut_fraction': 'Out of book maximum cut',
  'rules.out_of_book': 'Penalise time out of the book',
  rules: 'Rules',
  reason: 'Reason',
  enabled: 'Enabled',
  days: 'Days',
  start_time: 'Start (UTC)',
  end_time: 'End (UTC)',
  start: 'Start',
  end: 'End',
};

const labelOf = (path: (string | number)[]) => {
  const [first, ...rest] = path.map(String);
  const isBucket = first === 'b1' || first === 'b2';
  const key = isBucket ? rest.join('.') : [first, ...rest].join('.');
  const label = LABELS[key] ?? key;
  return isBucket ? `${first.toUpperCase()} ${label}` : label;
};

const listOf = (values: unknown[], unit = '') => (values.length ? `${values.join(', ')}${unit}` : 'none');

const formatValue = (path: (string | number)[], value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    return path.at(-1) === 'end_date' || path.at(-1) === 'start_date' ? 'Open' : '—';
  }
  const key = String(path.at(-1));
  if (key === 'active') return value ? 'Active' : 'Inactive';
  if (key === 'type') return PROGRAM_TYPE_LABELS[value as keyof typeof PROGRAM_TYPE_LABELS] ?? String(value);
  if (key === 'session') return SESSION_LABELS[String(value)] ?? String(value);
  if (key === 'start_date' || key === 'end_date') return dayLabel(String(value));
  if (key === 'start' || key === 'end') return formatUtcDateTime(String(value));
  if (key === 'days' && Array.isArray(value)) return listOf(value.map((day) => DOWNTIME_DAY_LABELS[day as DowntimeDay] ?? day));
  if (key === 'live' && Array.isArray(value)) return listOf(value.map((component) => COMPONENT_LABELS[component as RuleComponent] ?? component));
  if (Array.isArray(value)) return listOf(value, key.endsWith('_bps') ? ' bps' : '');
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (isPlainObject(value)) return 'Set';
  if (typeof value !== 'number') return String(value);
  if (key === 'bps') return `${value} bps`;
  if (key === 'kpi') return formatUsd(value, 0);
  if (key === 'compensation') return `${formatUsd(value, 0)} / month`;
  if (key === 'threshold' || key === 'exponent_start') return `${value}%`;
  if (key.endsWith('_fraction')) return formatPercent(value);
  if (key.endsWith('_ms')) return `${value} ms`;
  if (key.endsWith('_s')) return `${value} s`;
  return String(value);
};

const rowsBetween = (before: unknown, after: unknown): ChangeRow[] =>
  diffLeaves(before, after)
    .map(({ path, before: from, after: to }) => ({ label: labelOf(path), before: formatValue(path, from), after: formatValue(path, to) }))
    .filter((row) => row.before !== row.after);

const summaryRows = (program: ProgramInput, side: 'before' | 'after'): ChangeRow[] =>
  [
    { label: 'Variant', value: PROGRAM_TYPE_LABELS[program.type] ?? program.type },
    { label: 'B1', value: `${program.b1.bps} bps · ${formatUsd(program.b1.kpi, 0)}` },
    { label: 'B2', value: `${program.b2.bps} bps · ${formatUsd(program.b2.kpi, 0)}` },
    { label: 'Compensation', value: `${formatUsd(compensationOf(program), 0)} / month` },
  ].map(({ label, value }) => (side === 'after' ? { label, before: '—', after: value } : { label, before: value, after: '—' }));

const scheduleKey = (program: ProgramInput) => `${program.start_date.slice(0, 10)}|${program.session ?? ''}`;

const scheduleTitle = (instrument: string, program: ProgramInput) =>
  `${instrument} · ${dayLabel(program.start_date)} → ${dayLabel(program.end_date)}${program.session ? ` · ${SESSION_LABELS[program.session] ?? program.session}` : ''}`;

/**
 * Pairs each instrument's saved and edited schedules by start day and session. A lone unmatched pair is the same schedule
 * with its start moved, so it is compared rather than shown as removed and added.
 */
const scheduleGroups = (instrument: string, before: ProgramInput[], after: ProgramInput[]): ChangeGroup[] => {
  const savedByKey = new Map(before.map((program) => [scheduleKey(program), program]));
  const matched = new Set<string>();
  const groups: ChangeGroup[] = [];
  const unmatchedAfter: ProgramInput[] = [];

  for (const program of after) {
    const saved = savedByKey.get(scheduleKey(program));
    if (!saved) {
      unmatchedAfter.push(program);
      continue;
    }
    matched.add(scheduleKey(program));
    const rows = rowsBetween(saved, program);
    if (rows.length) groups.push({ key: `${instrument}:${scheduleKey(program)}`, title: scheduleTitle(instrument, program), kind: 'changed', rows });
  }
  const unmatchedBefore = before.filter((program) => !matched.has(scheduleKey(program)));

  if (unmatchedBefore.length === 1 && unmatchedAfter.length === 1 && unmatchedBefore[0].session === unmatchedAfter[0].session) {
    const [saved, program] = [unmatchedBefore[0], unmatchedAfter[0]];
    const rows = rowsBetween(saved, program);
    return rows.length ? [...groups, { key: `${instrument}:moved:${scheduleKey(program)}`, title: scheduleTitle(instrument, program), kind: 'changed', rows }] : groups;
  }
  return [
    ...groups,
    ...unmatchedAfter.map((program) => ({
      key: `${instrument}:added:${scheduleKey(program)}`,
      title: scheduleTitle(instrument, program),
      subtitle: 'New schedule',
      kind: 'added' as const,
      rows: summaryRows(program, 'after'),
    })),
    ...unmatchedBefore.map((program) => ({
      key: `${instrument}:removed:${scheduleKey(program)}`,
      title: scheduleTitle(instrument, program),
      subtitle: 'Removed schedule',
      kind: 'removed' as const,
      rows: summaryRows(program, 'before'),
    })),
  ];
};

const downtimeTitle = (instrument: string, rule: DowntimeRuleInput) => `${instrument} downtime · ${describeDowntimeRule(rule)}`;

const downtimeSummaryRows = (rule: DowntimeRuleInput, side: 'before' | 'after'): ChangeRow[] =>
  [
    { label: 'Reason', value: rule.reason },
    { label: 'Type', value: rule.type === 'recurring' ? 'Recurring' : 'One-off' },
    { label: 'Status', value: rule.enabled ? 'Enabled' : 'Disabled' },
  ].map(({ label, value }) => (side === 'after' ? { label, before: '—', after: value } : { label, before: value, after: '—' }));

/** Pairs each instrument's saved and edited downtime rules by id. */
const downtimeGroups = (instrument: string, before: DowntimeRuleInput[], after: DowntimeRuleInput[]): ChangeGroup[] => {
  const savedById = new Map(before.map((rule) => [rule.id, rule]));
  const editedIds = new Set(after.map(({ id }) => id));
  return [
    ...after.flatMap((rule): ChangeGroup[] => {
      const saved = savedById.get(rule.id);
      if (!saved) {
        return [{ key: `downtime:added:${rule.id}`, title: downtimeTitle(instrument, rule), subtitle: 'New downtime', kind: 'added', rows: downtimeSummaryRows(rule, 'after') }];
      }
      const rows = rowsBetween(saved, rule);
      return rows.length ? [{ key: `downtime:${rule.id}`, title: downtimeTitle(instrument, rule), kind: 'changed', rows }] : [];
    }),
    ...before
      .filter(({ id }) => !editedIds.has(id))
      .map((rule) => ({
        key: `downtime:removed:${rule.id}`,
        title: downtimeTitle(instrument, rule),
        subtitle: 'Removed downtime',
        kind: 'removed' as const,
        rows: downtimeSummaryRows(rule, 'before'),
      })),
  ];
};

/** What a save changes, grouped for the confirmation: the market maker's own fields, then each schedule, then each downtime rule. */
export const describeSettingsChanges = (before: MarketMakerInput, after: MarketMaker): ChangeGroup[] => {
  const pick = (document: MarketMakerInput | MarketMaker) => Object.fromEntries(MARKET_MAKER_FIELDS.map((field) => [field, document[field]]));
  const ownRows = rowsBetween(pick(before), pick(after));
  const savedPrograms = before.programs ?? {};
  const editedPrograms = after.programs ?? {};
  const instruments = [...new Set([...Object.keys(savedPrograms), ...Object.keys(editedPrograms)])].sort();
  const downtimeInstruments = [...new Set([...Object.keys(before.downtime ?? {}), ...Object.keys(after.downtime ?? {})])].sort();

  return [
    ...(ownRows.length ? [{ key: 'market-maker', title: 'Market maker', kind: 'changed' as const, rows: ownRows }] : []),
    ...instruments.flatMap((instrument) => scheduleGroups(instrument, savedPrograms[instrument] ?? [], editedPrograms[instrument] ?? [])),
    ...downtimeInstruments.flatMap((instrument) => downtimeGroups(instrument, before.downtime?.[instrument] ?? [], after.downtime?.[instrument] ?? [])),
  ];
};
